import React, { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { StatusApi, API, AuthApi, getAuthUser, setAuthSession, clearAuthSession } from "@/lib/apiClient";
import Sidebar from "@/components/Sidebar";
import Login from "@/components/Login";
import UserManager from "@/components/UserManager";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import LeadsTable from "@/components/LeadsTable";
import CampaignDialog from "@/components/CampaignDialog";
import CampaignsManager from "@/components/CampaignsManager";
import UserGuide from "@/components/UserGuide";
import StatsPanel from "@/components/StatsPanel";
import RecallAlert from "@/components/RecallAlert";
import SettingsDialog from "@/components/SettingsDialog";
import FunnelInbox from "@/components/FunnelInbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Settings, Download, Search, Radar, Building2, Phone, Mail, Loader2, Info, Upload, Sparkles, Menu, X, ListFilter, Layers, Send, BookOpen, Ban, BarChart3, Wrench, ChevronDown, Users, LogOut, KeyRound } from "lucide-react";

function fmtThousands(n) {
  return (n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-border rounded-md px-4 py-2.5 bg-white min-w-[110px]" data-testid={`stat-${label}`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold text-xl ${accent || ""}`}>{fmtThousands(value)}</div>
    </div>
  );
}

// Ricerca globale stile Google: trova un'azienda in tutti i comuni e ci salta sopra.
function GlobalSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tRef = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await StatusApi.globalSearch(q.trim());
        setResults(r || []); setOpen(true);
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);
  return (
    <div className="relative w-52 lg:w-72" data-testid="global-search">
      <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
        placeholder="Cerca azienda ovunque..." className="h-9 pl-8" data-testid="global-search-input" />
      {loading && <Loader2 className="h-4 w-4 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-72 max-h-80 overflow-auto bg-white border border-border rounded-md shadow-lg right-0" data-testid="global-search-results">
            {results.length === 0 && !loading && q.trim().length >= 2 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="global-search-empty">Nessun risultato</div>
            ) : (
              results.map((r) => (
                <button key={r.id} data-testid={`global-search-result-${r.id}`}
                  onClick={() => { onPick(r); setOpen(false); setQ(""); setResults([]); }}
                  className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border last:border-0 transition-colors">
                  <div className="text-sm font-semibold truncate">{r.company_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.comune}{r.address ? ` · ${r.address}` : ""}
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [authUser, setAuthUser] = useState(getAuthUser());
  const [authChecked, setAuthChecked] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [comuni, setComuni] = useState([]);
  const [counts, setCounts] = useState({});
  const [history, setHistory] = useState({});
  const [hideClosed, setHideClosed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stats, setStats] = useState({});

  const [query, setQuery] = useState("");
  const [source, setSource] = useState("free");
  const [deepSearch, setDeepSearch] = useState(false);
  const [scrapeEmails, setScrapeEmails] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [hiddenCategories, setHiddenCategories] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("hiddenCategories") || "[]")); } catch { return new Set(); }
  });
  const toggleCategory = (cat) => {
    setHiddenCategories((prev) => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      localStorage.setItem("hiddenCategories", JSON.stringify([...n]));
      return n;
    });
  };

  const [jobs, setJobs] = useState({});
  const pollRefs = useRef({});

  const loadComuni = useCallback(async () => {
    const c = await StatusApi.listComuni();
    setComuni(c);
    if (!selected && c.length) setSelected(c[0].name);
    return c;
  }, [selected]);

  const loadStats = useCallback(async () => {
    try {
      const s = await StatusApi.stats();
      setStats(s);
    } catch {}
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await StatusApi.getSettings();
      setSettings(s);
    } catch {}
  }, []);

  const loadLeads = useCallback(async () => {
    if (!selected) { setLeads([]); return; }
    setLoading(true);
    try {
      const params = { comune: selected };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (hideClosed) params.hide_closed = true;
      const data = await StatusApi.listLeads(params);
      setLeads(data);
    } catch {
      toast.error("Errore caricamento nominativi");
    } finally {
      setLoading(false);
    }
  }, [selected, statusFilter, search, hideClosed]);

  const refreshCounts = useCallback(async () => {    try {
      const counts = await StatusApi.comuniCounts();
      setCounts(counts || {});
    } catch {}
    try {
      setHistory(await StatusApi.scrapeHistory());
    } catch {}
  }, []);

  const runBackupNow = async () => {
    toast.info("Backup in corso...");
    try {
      const r = await StatusApi.runBackup();
      if (r.ok) toast.success(`Backup inviato a ${r.to}`);
      else if ((r.reason || "").toLowerCase().includes("domain")) toast.warning("Backup creato, ma email non inviata: verifica il dominio siacasaimmobiliare.it su Resend.");
      else toast.error("Backup non inviato: " + (r.reason || "config mancante"));
    } catch { toast.error("Errore durante il backup"); }
  };

  useEffect(() => {
    const bootstrap = async () => {
      // 1) SSO da SIACASA HUB: se l'URL contiene ?sso_token=..., login automatico.
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get("sso_token");
      if (ssoToken) {
        try {
          const { token, user } = await AuthApi.sso(ssoToken);
          setAuthSession(token, user);
          setAuthUser(user);
          return;
        } catch {
          // token non valido/scaduto -> prosegui con il flusso normale
        } finally {
          // Rimuovi subito il token dall'URL (cronologia/referer).
          params.delete("sso_token");
          const q = params.toString();
          window.history.replaceState({}, document.title, window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
        }
      }
      // 2) Sessione esistente
      try {
        const u = await AuthApi.me();
        setAuthUser(u);
        return;
      } catch {
        // In preview: accesso automatico come Stefano (comodità). In produzione: login normale.
        if (window.location.hostname.includes("preview.emergentagent.com")) {
          try {
            const { token, user } = await AuthApi.devLogin();
            setAuthSession(token, user);
            setAuthUser(user);
            return;
          } catch { /* fallback al login */ }
        }
        clearAuthSession();
        setAuthUser(null);
      }
    };
    bootstrap().finally(() => setAuthChecked(true));
    const onUnauth = () => setAuthUser(null);
    window.addEventListener("auth:unauthorized", onUnauth);
    return () => window.removeEventListener("auth:unauthorized", onUnauth);
  }, []);

  const doLogout = async () => {
    await AuthApi.logout();
    clearAuthSession();
    setAuthUser(null);
  };

  useEffect(() => { if (authChecked && authUser) { loadComuni(); loadSettings(); loadStats(); } }, [authChecked, authUser]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { if (comuni.length) refreshCounts(comuni); }, [comuni]);

  const startScrape = async () => {
    if (!selected) { toast.error("Seleziona un comune"); return; }
    const comune = selected;
    if (jobs[comune]?.status === "running") { toast.info(`Raccolta già in corso per ${comune}`); return; }
    const sourceMap = {
      free: ["paginegialle", "osm"],
      free_emergent: ["paginegialle", "paginebianche", "osm"],
      free_google: ["paginegialle", "osm", "google"],
    };
    const sources = sourceMap[source] || ["osm"];
    if (sources.includes("google") && !settings?.google_api_key_set) {
      toast.error("Configura prima la chiave API Google (o usa le fonti gratuite)");
      setSettingsOpen(true);
      return;
    }
    try {
      const { job_id } = await StatusApi.startScrape({
        comune,
        query: query,
        deep_search: deepSearch,
        scrape_emails: scrapeEmails,
        sources,
      });
      toast.success(`Raccolta avviata per ${comune}...`);
      setJobs((prev) => ({ ...prev, [comune]: { id: job_id, status: "running", progress: 0, found: 0 } }));
      pollJob(job_id, comune);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio raccolta");
    }
  };

  const pollJob = (jobId, comune) => {
    if (pollRefs.current[comune]) clearInterval(pollRefs.current[comune]);
    pollRefs.current[comune] = setInterval(async () => {
      try {
        const j = await StatusApi.getJob(jobId);
        setJobs((prev) => ({ ...prev, [comune]: j }));
        if (j.status === "completed" || j.status === "error") {
          clearInterval(pollRefs.current[comune]);
          delete pollRefs.current[comune];
          if (j.status === "completed") {
            if (j.found > 0) {
              toast.success(`${comune}: ${j.found} nuovi nominativi`);
            } else {
              toast.info(`${comune}: nessun nuovo nominativo`);
            }
            if (j.current && j.current.includes("non disponibili")) {
              toast.warning("Alcune fonti erano occupate; riprova più tardi.");
            }
          } else {
            toast.error(`Errore raccolta ${comune}: ${j.error || ""}`);
          }
          if (selectedRef.current === comune) loadLeads();
          refreshCounts();
          loadStats();
          setTimeout(() => setJobs((prev) => { const n = { ...prev }; delete n[comune]; return n; }), 4000);
        }
      } catch {}
    }, 2000);
  };

  useEffect(() => () => Object.values(pollRefs.current).forEach((t) => clearInterval(t)), []);

  const doExport = () => {
    const params = new URLSearchParams();
    if (selected) params.append("comune", selected);
    if (statusFilter !== "all") params.append("status", statusFilter);
    window.open(`${API}/leads/export?${params.toString()}`, "_blank");
  };

  const doEnrich = async () => {
    if (!selected) return;
    const comune = selected;
    if (jobs[comune]?.status === "running") { toast.info(`Operazione già in corso per ${comune}`); return; }
    try {
      const { job_id } = await StatusApi.startEnrich(comune);
      toast.success(`Ricerca contatti avviata per ${comune}...`);
      setJobs((prev) => ({ ...prev, [comune]: { id: job_id, status: "running", progress: 0, found: 0, current: "Ricerca contatti..." } }));
      pollJob(job_id, comune);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const doAiEnrich = async () => {
    if (!selected) return;
    const comune = selected;
    if (jobs[comune]?.status === "running") { toast.info(`Operazione già in corso per ${comune}`); return; }
    if (!settings?.gemini_api_key_set) {
      toast.error("Configura prima la chiave Gemini in Impostazioni");
      setSettingsOpen(true);
      return;
    }
    try {
      const { job_id } = await StatusApi.startAiEnrich(comune, 40);
      toast.success(`Ricerca contatti AI avviata per ${comune} (max 40)...`);
      setJobs((prev) => ({ ...prev, [comune]: { id: job_id, status: "running", progress: 0, found: 0, current: "Ricerca AI..." } }));
      pollJob(job_id, comune);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const onLeadChanged = (id, data) => {
    if (id && data) {
      // Modifica inline: aggiorna solo la riga in memoria (niente reload, lo scroll resta fermo)
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
      loadStats();
    } else {
      loadLeads();
      loadStats();
    }
  };

  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await StatusApi.importTelemaco(file);
      toast.success(`Importate ${res.added} aziende (${(res.comuni || []).join(", ")})`);
      const c = await loadComuni();
      refreshCounts();
      loadStats();
      if (res.comuni && res.comuni[0]) setSelected(res.comuni[0]);
      else loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore importazione CSV");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const doDedupe = async () => {
    if (deduping) return;
    setDeduping(true);
    try {
      const r = await StatusApi.dedupeLeads();
      toast.success(r.removed > 0 ? `${r.removed} doppioni uniti` : "Nessun doppione trovato");
      loadLeads();
      refreshCounts();
      loadStats();
    } catch (e) {
      toast.error("Errore nella rimozione doppioni");
    } finally {
      setDeduping(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = (ids, checked) => setSelectedIds((prev) => {
    const n = new Set(prev);
    ids.forEach((id) => (checked ? n.add(id) : n.delete(id)));
    return n;
  });

  const currentJob = jobs[selected];
  const isRunning = currentJob && currentJob.status === "running";
  const runningComuni = Object.entries(jobs).filter(([, j]) => j?.status === "running").map(([c]) => c);
  const googleInvolved = source === "free_google";
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const categoryCounts = leads.reduce((acc, l) => {
    const c = l.category || "Senza categoria";
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const allCategories = Object.keys(categoryCounts).sort((a, b) => a.localeCompare(b));
  const visibleLeads = leads.filter((l) => !hiddenCategories.has(l.category || "Senza categoria"));

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!authUser) {
    return <Login onSuccess={(u) => setAuthUser(u)} />;
  }
  if (authUser.must_change_password) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40" data-testid="forced-password-screen">
        <ChangePasswordDialog
          open
          forced
          onOpenChange={() => {}}
          onChanged={() => {
            const u = { ...authUser, must_change_password: false };
            setAuthUser(u);
            localStorage.setItem("auth_user", JSON.stringify(u));
          }}
        />
      </div>
    );
  }

  return (
    <div className="App flex h-screen overflow-hidden bg-muted/30">
      <Toaster position="top-right" richColors closeButton duration={5000} />
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}
      <div className={`fixed inset-y-0 left-0 z-40 md:relative md:z-auto transform transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <Sidebar
          comuni={comuni}
          selected={selected}
          onSelect={(name) => { setSelected(name); setSidebarOpen(false); setSelectedIds(new Set()); }}
          onChanged={() => loadComuni().then((c) => refreshCounts(c))}
          counts={counts}
          history={history}
          jobs={jobs}
        />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Header */}
        <header className="border-b border-border bg-white px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle"
              className="md:hidden p-2 -ml-1 rounded-md hover:bg-muted text-foreground">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Radar className="h-6 w-6 text-primary" />
              <div>
                <h1 className="font-display font-black tracking-tight text-xl leading-none">LeadRadar</h1>
                <p className="text-xs text-muted-foreground">Raccolta nominativi aziende · Telemarketing</p>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="hidden lg:flex items-center gap-2">
                <StatCard label="Totale" value={stats.total || 0} />
                <StatCard label="Con tel." value={stats.with_phone || 0} accent="text-primary" />
                <StatCard label="Con email" value={stats.with_email || 0} accent="text-primary" />
                <StatCard label="Con sito" value={stats.with_website || 0} accent="text-primary" />
              </div>
              <GlobalSearch onPick={(r) => {
                setStatusFilter("all");
                setHideClosed(false);
                setSelected(r.comune);
                setSearch(r.company_name);
              }} />
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="open-settings-btn" className="h-9">
                <Settings className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Impostazioni</span>
              </Button>
              <FunnelInbox />
              <Button variant="outline" size="sm" onClick={() => setCampaignsOpen(true)} data-testid="open-campaigns-btn" className="h-9">
                <Mail className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Campagne</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setStatsOpen(true)} data-testid="open-stats-btn" className="h-9">
                <BarChart3 className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Statistiche</span>
              </Button>
              <RecallAlert onGoToComune={(r) => { setStatusFilter("all"); setHideClosed(false); setSelected(r.comune); setSearch(r.company_name); }} />
              <Button variant="outline" size="sm" onClick={() => setGuideOpen(true)} data-testid="open-guide-btn" className="h-9">
                <BookOpen className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Guida</span>
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-telemaco-btn" className="h-9">
                {importing ? <Loader2 className="h-4 w-4 md:mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 md:mr-1.5" />}
                <span className="hidden md:inline">Importa CSV Telemaco</span>
              </Button>
              {authUser?.role === "admin" && (
                <Button variant="outline" size="sm" onClick={() => setUsersOpen(true)} data-testid="open-users-btn" className="h-9">
                  <Users className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Utenti</span>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-1.5 pl-1.5" data-testid="user-menu-btn">
                    <div className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[11px] font-bold">
                      {(authUser?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="text-xs font-semibold" data-testid="current-user">{authUser?.name}</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white w-52">
                  <div className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">Operatore attivo</div>
                  <DropdownMenuItem data-testid="menu-change-password" onClick={() => setChangePwOpen(true)}>
                    <KeyRound className="h-4 w-4 mr-2" /> Cambia password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="logout-btn" onClick={doLogout} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                    <LogOut className="h-4 w-4 mr-2" /> Esci dall'app
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Scrape control bar */}
        <div className="border-b border-border bg-white px-4 md:px-6 py-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Comune selezionato</label>
              <div className="flex items-center gap-1.5 font-display font-bold text-lg h-9">
                {(() => {
                  const st = comuni.find((c) => c.name === selected)?.stemma_url;
                  return st ? (
                    <img src={st} alt="" data-testid="selected-comune-stemma"
                      className="h-6 w-6 object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <Building2 className="h-4 w-4 text-primary" />
                  );
                })()}
                {selected || "—"}
              </div>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fonte dati</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-9 w-full sm:w-56" data-testid="source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="free">Gratis ⭐ (OSM + PagineGialle)</SelectItem>
                  <SelectItem value="free_emergent">Gratis + € Emergent AI (PagineBianche)</SelectItem>
                  <SelectItem value="free_google">Gratis + € Google (Google Places)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {googleInvolved && (
              <div className="flex flex-col gap-1 flex-1 min-w-[180px] max-w-xs">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Filtro categoria (solo Google)</label>
                <Input
                  data-testid="scrape-query-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="es. ristoranti, officine..."
                  className="h-9"
                  disabled={deepSearch}
                />
              </div>
            )}
            <div className="flex flex-col gap-2 pb-1">
              {googleInvolved && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="deep-search-label">
                  <Checkbox checked={deepSearch} onCheckedChange={(v) => setDeepSearch(!!v)} data-testid="deep-search-checkbox" />
                  Ricerca approfondita Google (tutte le categorie)
                </label>
              )}
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={scrapeEmails} onCheckedChange={(v) => setScrapeEmails(!!v)} data-testid="scrape-emails-checkbox" />
                Cerca email/telefoni dai siti web
              </label>
            </div>
            <Button onClick={startScrape} disabled={isRunning || !selected} data-testid="start-scrape-btn"
              className="h-9 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              {isRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Radar className="h-4 w-4 mr-1.5" />}
              {isRunning ? "Raccolta in corso..." : "Avvia raccolta"}
            </Button>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="cost-estimate">
            <Info className="h-3 w-3" />
            {source === "free" ? (
              <span className="text-emerald-600 font-semibold">100% gratis, nessun costo. Ampia copertura di aziende con telefono.</span>
            ) : source === "free_emergent" ? (
              <span>Gratis + <b>Emergent AI</b> (PagineBianche): costo AI di pochi <b>centesimi</b> per comune per estrarre telefoni/referenti nascosti.</span>
            ) : deepSearch ? (
              <span>Gratis + <b>Google</b> approfondita: circa <b>15-45 chiamate</b> API (~<b>0,50-1,50$</b>), dentro i 200$/mese gratuiti.</span>
            ) : (
              <span>Gratis + <b>Google</b>: circa <b>1-3 chiamate</b> API (~<b>0,03-0,10$</b>), dentro i 200$/mese gratuiti.</span>
            )}
          </div>
          {currentJob && (
            <div className="mt-3" data-testid="scrape-progress">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground truncate">{currentJob.current || "Avvio..."}</span>
                <span className="font-mono-plex">{currentJob.found || 0} trovati · {currentJob.progress || 0}%</span>
              </div>
              <Progress value={currentJob.progress || 0} className="h-1.5" />
            </div>
          )}
          {runningComuni.filter((c) => c !== selected).length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-primary" data-testid="other-running-jobs">
              <Loader2 className="h-3 w-3 animate-spin" />
              Raccolte in corso anche su: {runningComuni.filter((c) => c !== selected).join(", ")}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="border-b border-border bg-white px-6 py-2.5 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              data-testid="search-leads-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca azienda, telefono, email..."
              className="h-9 pl-8 pr-8"
            />
            {search && (
              <button data-testid="clear-search-btn" onClick={() => setSearch("")} title="Azzera ricerca"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="da fare">da fare</SelectItem>
              <SelectItem value="da richiamare">da richiamare</SelectItem>
              <SelectItem value="appuntamento fissato">appuntamento fissato</SelectItem>
              <SelectItem value="non interessato">non interessato</SelectItem>
              <SelectItem value="acquisito">acquisito</SelectItem>
              <SelectItem value="concluso">concluso</SelectItem>
              <SelectItem value="numero errato/inesistente">numero errato/inesistente</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="category-filter-btn">
                <ListFilter className="h-4 w-4 mr-1.5" />
                Categorie{hiddenCategories.size > 0 ? ` (${hiddenCategories.size} nascoste)` : ""}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 bg-white max-h-80 overflow-auto" align="start">
              <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-border">
                <span className="text-xs font-semibold">Mostra categorie</span>
                {hiddenCategories.size > 0 && (
                  <button className="text-[11px] text-primary hover:underline" data-testid="show-all-categories"
                    onClick={() => { setHiddenCategories(new Set()); localStorage.setItem("hiddenCategories", "[]"); }}>
                    Mostra tutte
                  </button>
                )}
              </div>
              {allCategories.length === 0 && <div className="text-xs text-muted-foreground px-1 py-2">Nessuna categoria</div>}
              {allCategories.map((cat) => (
                <label key={cat} data-testid={`category-toggle-${cat}`}
                  className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-muted rounded">
                  <Checkbox checked={!hiddenCategories.has(cat)} onCheckedChange={() => toggleCategory(cat)} />
                  <span className="flex-1 truncate">{cat}</span>
                  <span className="text-[11px] text-muted-foreground">{categoryCounts[cat]}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span data-testid="leads-count">{visibleLeads.length} nominativi{hiddenCategories.size > 0 ? ` (di ${leads.length})` : ""}</span>
            <Button onClick={() => setCampaignOpen(true)} disabled={selectedIds.size === 0} data-testid="send-campaign-btn" className="h-9">
              <Send className="h-4 w-4 mr-1.5" /> Invia email ({selectedIds.size})
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-9" data-testid="actions-menu-btn">
                  <Wrench className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Azioni</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-56">
                <DropdownMenuItem data-testid="action-hide-closed" onClick={() => setHideClosed((v) => !v)}>
                  <Ban className="h-4 w-4 mr-2" /> {hideClosed ? "Mostra cessate" : "Nascondi cessate"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="action-enrich" disabled={!leads.length || isRunning} onClick={doEnrich}>
                  <Mail className="h-4 w-4 mr-2" /> Trova contatti mancanti
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="action-ai-enrich" disabled={!leads.length || isRunning} onClick={doAiEnrich}>
                  <Sparkles className="h-4 w-4 mr-2" /> Cerca contatti AI
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="action-dedupe" disabled={deduping || !leads.length || isRunning} onClick={doDedupe}>
                  <Layers className="h-4 w-4 mr-2" /> {deduping ? "Rimozione..." : "Rimuovi doppioni"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={doExport} disabled={!leads.length} data-testid="export-btn" className="h-9">
              <Download className="h-4 w-4 mr-1.5" /> Esporta CSV/Excel
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
            </div>
          ) : (
            <LeadsTable leads={visibleLeads} onChanged={onLeadChanged}
              selectedIds={selectedIds} onToggle={toggleSelect} onToggleAll={toggleAll} />
          )}
        </div>
      </main>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSaved={loadSettings}
        onBackup={runBackupNow}
      />
      <CampaignDialog
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        leads={visibleLeads.filter((l) => selectedIds.has(l.id))}
        onSent={() => { setSelectedIds(new Set()); loadLeads(); }}
      />
      <CampaignsManager open={campaignsOpen} onOpenChange={setCampaignsOpen} />
      <UserManager open={usersOpen} onOpenChange={setUsersOpen} currentUserId={authUser?.id} />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
      <UserGuide open={guideOpen} onOpenChange={setGuideOpen} />
      <StatsPanel open={statsOpen} onOpenChange={setStatsOpen} />
    </div>
  );
}
